import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { WHATSAPP_URL, TEL_URL, CONTACT } from "../data/site";

// Official WhatsApp icon (Meta/WhatsApp brand — inline SVG)
const WhatsAppIcon = ({ className = "" }) => (
    <svg
        viewBox="0 0 32 32"
        fill="currentColor"
        aria-hidden="true"
        className={className}
    >
        <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.077 0-.156-.039-.238-.077-.492-.246-1.415-.643-2.756-1.83-.804-.71-1.34-1.598-1.499-1.902-.16-.301-.014-.469.126-.607.13-.13.291-.334.437-.501.145-.169.194-.29.29-.484.098-.194.05-.36-.024-.502-.077-.14-.652-1.585-.893-2.171-.24-.586-.484-.484-.652-.484l-.556-.014c-.194 0-.501.072-.766.362-.263.288-1.006.981-1.006 2.393 0 1.412 1.03 2.775 1.175 2.966.144.19 2.055 3.14 4.98 4.406.694.3 1.235.481 1.658.615.696.226 1.33.194 1.83.117.559-.083 1.717-.704 1.958-1.383.242-.68.242-1.262.17-1.383-.075-.121-.269-.194-.564-.336m-5.446 7.443h-.014a9.87 9.87 0 01-5.032-1.383l-.36-.214-3.744.984 1-3.65-.234-.376a9.833 9.833 0 01-1.51-5.256c.001-5.44 4.427-9.868 9.892-9.868a9.798 9.798 0 016.987 2.892 9.815 9.815 0 012.892 6.99c-.002 5.44-4.428 9.869-9.891 9.869M23.554 8.464a11.816 11.816 0 00-8.418-3.487c-6.554 0-11.89 5.335-11.892 11.893a11.86 11.86 0 001.588 5.945L3.104 28.867l6.208-1.629a11.883 11.883 0 005.685 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0023.554 8.464"/>
    </svg>
);

export default function FloatingActions() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setShow(true), 700);
        return () => clearTimeout(t);
    }, []);

    if (!show) return null;

    return (
        <>
            {/* Desktop / tablet — vertical stack (phone on top, WA on bottom) */}
            <div
                data-testid="floating-actions"
                className="hidden md:flex fixed bottom-6 right-6 z-40 flex-col gap-3 items-center"
            >
                {/* Phone (gold gradient) */}
                <a
                    href={TEL_URL}
                    aria-label={`Ara: ${CONTACT.phone}`}
                    data-testid="floating-phone"
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-[#f5d685] via-[#c9a24b] to-[#8a6a28] text-[#0a0a0c] shadow-xl hover:scale-110 transition-transform"
                >
                    <Phone className="w-6 h-6" fill="currentColor" strokeWidth={0} />
                    <span
                        className="absolute inset-0 rounded-full ring-2 ring-[#c9a24b]/50"
                        style={{
                            animation: "pulseGold 2.4s infinite",
                        }}
                    />
                    <span className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#0a0a0c] text-[#f5d685] text-[12px] font-medium px-3 py-1.5 rounded-lg border border-[#c9a24b]/30 opacity-0 group-hover:opacity-100 transition">
                        Hemen Ara
                    </span>
                </a>

                {/* WhatsApp (green) */}
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`WhatsApp: ${CONTACT.whatsapp}`}
                    data-testid="floating-whatsapp"
                    className="group relative w-14 h-14 rounded-full flex items-center justify-center bg-[#25D366] text-white shadow-xl hover:scale-110 transition-transform"
                >
                    <WhatsAppIcon className="w-7 h-7" />
                    <span
                        className="absolute inset-0 rounded-full ring-2 ring-[#25D366]/60"
                        style={{
                            animation: "pulseGreen 2s infinite",
                        }}
                    />
                    <span className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#0a0a0c] text-[#25D366] text-[12px] font-medium px-3 py-1.5 rounded-lg border border-[#25D366]/40 opacity-0 group-hover:opacity-100 transition">
                        WhatsApp'tan Yaz
                    </span>
                </a>
            </div>

            {/* Mobile bottom sticky bar */}
            <div
                data-testid="mobile-cta-bar"
                className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#23232b] bg-[#0a0a0c]/95 backdrop-blur-lg p-3 grid grid-cols-2 gap-2"
                style={{
                    paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
                }}
            >
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="btn !py-3 !text-[13px] bg-[#25D366] text-white hover:bg-[#1fbe5a] border border-[#25D366]"
                >
                    <WhatsAppIcon className="w-4 h-4" />
                    WhatsApp
                </a>
                <a href={TEL_URL} className="btn btn-gold !py-3 !text-[13px]">
                    <Phone className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                    Hemen Ara
                </a>
            </div>

            {/* Keyframes for pulses */}
            <style>{`
                @keyframes pulseGold {
                    0% { transform: scale(1); opacity: 0.85; }
                    100% { transform: scale(1.55); opacity: 0; }
                }
                @keyframes pulseGreen {
                    0% { transform: scale(1); opacity: 0.85; }
                    100% { transform: scale(1.55); opacity: 0; }
                }
            `}</style>
        </>
    );
}
